import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Copy, Check } from "lucide-react";
import { useState } from "react";

// Generate unique failure code
export function generateFailureCode(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 7);
  return `ERR-${timestamp}-${random}`.toUpperCase();
}

// Copy button component
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Failed to copy
    }
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleCopy}
      className="h-6 w-6 p-0"
    >
      {copied ? (
        <Check className="h-3 w-3 text-green-500" />
      ) : (
        <Copy className="h-3 w-3" />
      )}
    </Button>
  );
}

interface ErrorToastOptions {
  title?: string;
  message: string;
  technicalDetails?: string;
  duration?: number;
}

/**
 * Show an error toast with a failure code and copy button
 */
export function showErrorToast({
  title = "Error",
  message,
  technicalDetails,
  duration = 10000,
}: ErrorToastOptions) {
  const failureCode = generateFailureCode();
  
  // Build the full error text for copying
  const fullErrorText = [
    `Failure Code: ${failureCode}`,
    `Title: ${title}`,
    `Message: ${message}`,
    technicalDetails ? `Technical Details: ${technicalDetails}` : null,
    `Timestamp: ${new Date().toISOString()}`,
  ]
    .filter(Boolean)
    .join("\n");

  toast({
    variant: "destructive",
    title,
    description: (
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <p className="flex-1">{message}</p>
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className="text-xs opacity-70 font-mono">{failureCode}</span>
            <CopyButton text={fullErrorText} />
          </div>
        </div>
        {technicalDetails && (
          <details className="mt-2">
            <summary className="text-xs cursor-pointer opacity-70 hover:opacity-100">
              Technical details
            </summary>
            <pre className="mt-1 text-xs opacity-70 whitespace-pre-wrap font-mono">
              {technicalDetails}
            </pre>
          </details>
        )}
      </div>
    ),
    duration,
  });

  // Log error to database asynchronously (don't await to avoid blocking UI)
  import('@/lib/errorLogger').then(({ logError }) => {
    logError({
      errorCode: failureCode,
      title,
      message,
      technicalDetails,
      severity: 'error',
    });
  });

  return failureCode;
}

/**
 * Show a success toast with copy button (optional)
 */
export function showSuccessToast({
  title = "Success",
  message,
  duration = 3000,
}: {
  title?: string;
  message: string;
  duration?: number;
}) {
  toast({
    title,
    description: message,
    duration,
  });
}
